// Settings › Leaders Bonus (Alex, 2026-09-28): the pay periods and KPI
// targets behind the Admin Dashboard's Leaders Bonus view. One list per
// warehouse; each period carries an on-time percentage and two PER
// WORKING DAY item targets (booked in, put away). The server multiplies
// the daily figures by the period's working days (Mon–Fri minus UK bank
// holidays) — the numbers shown in the "Period targets" column and on the
// dashboard's cards.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Pencil, Plus, RotateCcw, Trash2, X } from "lucide-react";
import { toast } from "react-toastify";

import { useTranslation } from "../../i18n";
import { apiFetch } from "../../api/apiFetch";
import {
    DEFAULT_LEADERS_BONUS_LADDERS,
    LEADERS_BONUS_METRICS,
    LeadersBonusLadders,
    LeadersBonusMetric,
    LeadersBonusOverlapError,
    LeadersBonusPeriod,
    LeadersBonusPeriodInput,
    createLeadersBonusPeriod,
    deleteLeadersBonusPeriod,
    fetchLeadersBonusPeriods,
    updateLeadersBonusPeriod,
} from "../../api/leadersBonus";

interface AllSettingsRow { warehouse: string; }

// Ladder steps are edited as strings so a half-typed number never snaps.
type LadderStepDraft = { from: string; points: string };
type LadderDraft = Record<LeadersBonusMetric, LadderStepDraft[]>;

type Draft = {
    id: number | null;
    warehouse: string;
    starts_on: string;
    ends_on: string;
    on_time_target_pct: string;
    booked_in_daily_target: string;
    put_away_daily_target: string;
    ladders: LadderDraft;
};

const laddersToDraft = (ladders: LeadersBonusLadders): LadderDraft => ({
    on_time: ladders.on_time.map((s) => ({ from: String(s.from), points: String(s.points) })),
    booked_in: ladders.booked_in.map((s) => ({ from: String(s.from), points: String(s.points) })),
    put_away: ladders.put_away.map((s) => ({ from: String(s.from), points: String(s.points) })),
});
const draftToLadders = (draft: LadderDraft): LeadersBonusLadders => ({
    on_time: draft.on_time.map((s) => ({ from: Number(s.from), points: Number(s.points) })),
    booked_in: draft.booked_in.map((s) => ({ from: Number(s.from), points: Number(s.points) })),
    put_away: draft.put_away.map((s) => ({ from: Number(s.from), points: Number(s.points) })),
});
// Mirrors the server's validateLadders: 1–10 steps, ascending in both
// threshold and points, on-time thresholds capped at 100.
const ladderValid = (metric: LeadersBonusMetric, steps: LadderStepDraft[]): boolean => {
    if (steps.length < 1 || steps.length > 10) return false;
    let prevFrom = -Infinity;
    let prevPts = -Infinity;
    for (const s of steps) {
        const from = Number(s.from);
        const pts = Number(s.points);
        if (s.from.trim() === "" || !Number.isFinite(from) || from < 0 || from > (metric === "on_time" ? 100 : 1000)) return false;
        if (s.points.trim() === "" || !Number.isInteger(pts) || pts < 0) return false;
        if (!(from > prevFrom) || pts < prevPts) return false;
        prevFrom = from;
        prevPts = pts;
    }
    return true;
};
const LADDER_TITLE_KEY: Record<LeadersBonusMetric, string> = {
    on_time: "dashboard_team_on_time_rate",
    booked_in: "dashboard_lb_booked_in",
    put_away: "dashboard_lb_put_away",
};

const fmtInt = (n: number) => n.toLocaleString();
const fmtDate = (iso: string) => {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { day: "2-digit", month: "2-digit", year: "numeric" });
};
const isoShift = (iso: string, days: number) => {
    const [y, m, d] = iso.split("-").map(Number);
    const out = new Date(y, m - 1, d + days);
    const mm = String(out.getMonth() + 1).padStart(2, "0");
    const dd = String(out.getDate()).padStart(2, "0");
    return `${out.getFullYear()}-${mm}-${dd}`;
};

// A fresh period starts the day after the warehouse's latest one ends
// (or today) and runs a month; targets copy the latest period so a
// leader only edits what changed.
const draftFor = (warehouse: string, latest: LeadersBonusPeriod | undefined, today: string): Draft => {
    const start = latest && latest.ends_on >= today ? isoShift(latest.ends_on, 1) : today;
    return {
        id: null,
        warehouse,
        starts_on: start,
        ends_on: isoShift(start, 29),
        on_time_target_pct: String(latest?.on_time_target_pct ?? 98),
        booked_in_daily_target: String(latest?.booked_in_daily_target ?? 0),
        put_away_daily_target: String(latest?.put_away_daily_target ?? 0),
        ladders: laddersToDraft(latest?.points_ladders ?? DEFAULT_LEADERS_BONUS_LADDERS),
    };
};

const draftOf = (p: LeadersBonusPeriod): Draft => ({
    id: p.id,
    warehouse: p.warehouse,
    starts_on: p.starts_on,
    ends_on: p.ends_on,
    on_time_target_pct: String(p.on_time_target_pct),
    booked_in_daily_target: String(p.booked_in_daily_target),
    put_away_daily_target: String(p.put_away_daily_target),
    ladders: laddersToDraft(p.points_ladders ?? DEFAULT_LEADERS_BONUS_LADDERS),
});

const LeadersBonusSettings = () => {
    const { t } = useTranslation();
    const [warehouses, setWarehouses] = useState<string[]>([]);
    const [periods, setPeriods] = useState<LeadersBonusPeriod[]>([]);
    const [today, setToday] = useState<string>("");
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState<Draft | null>(null);
    const [saving, setSaving] = useState(false);
    const [deleteId, setDeleteId] = useState<number | null>(null);
    const [deleting, setDeleting] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const [whRes, list] = await Promise.all([
                apiFetch(`/settings`, {}),
                fetchLeadersBonusPeriods(),
            ]);
            if (!whRes.ok) throw new Error("load");
            const whRows: AllSettingsRow[] = await whRes.json();
            setWarehouses(whRows.map((r) => r.warehouse).sort());
            setPeriods(list.periods);
            setToday(list.today);
        } catch {
            toast.error(t("server_error"), { toastId: "leaders-bonus-load-err" });
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { refresh(); }, [refresh]);

    const byWarehouse = useMemo(() => {
        const map: Record<string, LeadersBonusPeriod[]> = {};
        for (const wh of warehouses) map[wh] = [];
        for (const p of periods) {
            if (!map[p.warehouse]) map[p.warehouse] = [];
            map[p.warehouse].push(p);
        }
        for (const wh of Object.keys(map)) map[wh].sort((a, b) => (a.starts_on < b.starts_on ? 1 : -1));
        return map;
    }, [warehouses, periods]);

    const statusOf = (p: LeadersBonusPeriod): "current" | "upcoming" | "ended" => {
        if (!today) return "upcoming";
        if (p.starts_on <= today && p.ends_on >= today) return "current";
        return p.starts_on > today ? "upcoming" : "ended";
    };

    const draftValid = (d: Draft) => {
        if (!d.starts_on || !d.ends_on || d.ends_on < d.starts_on) return false;
        const pct = Number(d.on_time_target_pct);
        const booked = Number(d.booked_in_daily_target);
        const put = Number(d.put_away_daily_target);
        return Number.isFinite(pct) && pct >= 0 && pct <= 100
            && Number.isInteger(booked) && booked >= 0
            && Number.isInteger(put) && put >= 0
            && LEADERS_BONUS_METRICS.every((m) => ladderValid(m, d.ladders[m]));
    };

    const handleSave = async () => {
        if (!draft || !draftValid(draft)) return;
        const input: LeadersBonusPeriodInput = {
            warehouse: draft.warehouse,
            starts_on: draft.starts_on,
            ends_on: draft.ends_on,
            on_time_target_pct: Number(draft.on_time_target_pct),
            booked_in_daily_target: Number(draft.booked_in_daily_target),
            put_away_daily_target: Number(draft.put_away_daily_target),
            points_ladders: draftToLadders(draft.ladders),
        };
        setSaving(true);
        try {
            const saved = draft.id === null
                ? await createLeadersBonusPeriod(input)
                : await updateLeadersBonusPeriod(draft.id, input);
            setPeriods((prev) => [...prev.filter((p) => p.id !== saved.id), saved]);
            setDraft(null);
            toast.success(t("leaders_bonus_saved"), { toastId: "leaders-bonus-saved" });
        } catch (err) {
            if (err instanceof LeadersBonusOverlapError) {
                toast.error(t("leaders_bonus_overlap", {
                    from: err.conflict.starts_on ? fmtDate(err.conflict.starts_on) : "?",
                    to: err.conflict.ends_on ? fmtDate(err.conflict.ends_on) : "?",
                }), { toastId: "leaders-bonus-overlap" });
            } else {
                toast.error(t("server_error"), { toastId: "leaders-bonus-save-err" });
            }
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id: number) => {
        setDeleting(true);
        try {
            await deleteLeadersBonusPeriod(id);
            setPeriods((prev) => prev.filter((p) => p.id !== id));
            setDeleteId(null);
            toast.success(t("leaders_bonus_deleted"), { toastId: "leaders-bonus-deleted" });
        } catch {
            toast.error(t("server_error"), { toastId: "leaders-bonus-delete-err" });
        } finally {
            setDeleting(false);
        }
    };

    const field = (key: keyof Draft, value: string) =>
        setDraft((d) => (d ? { ...d, [key]: value } : d));

    const form = (d: Draft) => (
        <div className="lb-form" role="form" aria-label={d.id === null ? t("leaders_bonus_add_period") : t("edit")}>
            <div className="lb-form-grid">
                <label className="lb-form-field">
                    <span>{t("leaders_bonus_starts_on")}</span>
                    <input
                        type="date"
                        className="admin-settings-input"
                        value={d.starts_on}
                        onChange={(e) => field("starts_on", e.target.value)}
                    />
                </label>
                <label className="lb-form-field">
                    <span>{t("leaders_bonus_ends_on")}</span>
                    <input
                        type="date"
                        className="admin-settings-input"
                        value={d.ends_on}
                        min={d.starts_on}
                        onChange={(e) => field("ends_on", e.target.value)}
                    />
                </label>
                <label className="lb-form-field">
                    <span>{t("leaders_bonus_on_time_target")}</span>
                    <span className="lb-form-input-unit">
                        <input
                            type="number"
                            className="admin-settings-input"
                            inputMode="decimal"
                            min={0}
                            max={100}
                            step={0.5}
                            value={d.on_time_target_pct}
                            onChange={(e) => field("on_time_target_pct", e.target.value)}
                        />
                        <span>%</span>
                    </span>
                </label>
                <label className="lb-form-field">
                    <span>{t("leaders_bonus_booked_in_target")}</span>
                    <input
                        type="number"
                        className="admin-settings-input"
                        inputMode="numeric"
                        min={0}
                        step={1}
                        value={d.booked_in_daily_target}
                        onChange={(e) => field("booked_in_daily_target", e.target.value)}
                    />
                </label>
                <label className="lb-form-field">
                    <span>{t("leaders_bonus_put_away_target")}</span>
                    <input
                        type="number"
                        className="admin-settings-input"
                        inputMode="numeric"
                        min={0}
                        step={1}
                        value={d.put_away_daily_target}
                        onChange={(e) => field("put_away_daily_target", e.target.value)}
                    />
                </label>
            </div>
            <details className="lb-ladders">
                <summary>{t("leaders_bonus_points_title")}</summary>
                <div className="lb-ladders-body">
                    <p className="admin-settings-section-hint lb-form-hint">{t("leaders_bonus_points_hint")}</p>
                    <div className="lb-ladders-grid">
                        {LEADERS_BONUS_METRICS.map((metric) => {
                            const steps = d.ladders[metric];
                            const setSteps = (next: LadderStepDraft[]) =>
                                setDraft((cur) => (cur ? { ...cur, ladders: { ...cur.ladders, [metric]: next } } : cur));
                            const invalid = !ladderValid(metric, steps);
                            return (
                                <div key={metric} className={`lb-ladder${invalid ? " lb-ladder--invalid" : ""}`}>
                                    <h5 className="lb-ladder-title">
                                        {t(LADDER_TITLE_KEY[metric])}{" "}
                                        <span className="lb-ladder-unit">
                                            ({t(metric === "on_time" ? "leaders_bonus_ladder_unit_rate" : "leaders_bonus_ladder_unit_target")})
                                        </span>
                                    </h5>
                                    <div className="lb-ladder-row lb-ladder-row--head">
                                        <span>{t("leaders_bonus_ladder_from")}</span>
                                        <span>{t("leaders_bonus_ladder_points")}</span>
                                        <span aria-hidden="true" />
                                    </div>
                                    {steps.map((s, i) => (
                                        <div key={i} className="lb-ladder-row">
                                            <input
                                                type="number"
                                                className="admin-settings-input"
                                                inputMode="decimal"
                                                min={0}
                                                max={metric === "on_time" ? 100 : 1000}
                                                step={0.5}
                                                value={s.from}
                                                aria-label={t("leaders_bonus_ladder_from")}
                                                onChange={(e) => setSteps(steps.map((x, j) => (j === i ? { ...x, from: e.target.value } : x)))}
                                            />
                                            <input
                                                type="number"
                                                className="admin-settings-input"
                                                inputMode="numeric"
                                                min={0}
                                                step={1}
                                                value={s.points}
                                                aria-label={t("leaders_bonus_ladder_points")}
                                                onChange={(e) => setSteps(steps.map((x, j) => (j === i ? { ...x, points: e.target.value } : x)))}
                                            />
                                            <button
                                                type="button"
                                                className="dispatch-admin-row-icon dispatch-admin-row-icon--danger"
                                                title={t("leaders_bonus_ladder_remove_step")}
                                                aria-label={t("leaders_bonus_ladder_remove_step")}
                                                disabled={steps.length <= 1}
                                                onClick={() => setSteps(steps.filter((_, j) => j !== i))}
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                    ))}
                                    <div className="lb-ladder-actions">
                                        <button
                                            type="button"
                                            className="btn btn-ghost btn-sm"
                                            disabled={steps.length >= 10}
                                            onClick={() => {
                                                const last = steps[steps.length - 1];
                                                setSteps([...steps, {
                                                    from: last ? String(Number(last.from) + (metric === "on_time" ? 0.5 : 5)) : "0",
                                                    points: last ? String(Number(last.points) + 2) : "0",
                                                }]);
                                            }}
                                        >
                                            <Plus size={14} aria-hidden="true" />
                                            {t("leaders_bonus_ladder_add_step")}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm lb-ladders-reset"
                        onClick={() => setDraft((cur) => (cur ? { ...cur, ladders: laddersToDraft(DEFAULT_LEADERS_BONUS_LADDERS) } : cur))}
                    >
                        <RotateCcw size={14} aria-hidden="true" />
                        {t("leaders_bonus_ladder_reset")}
                    </button>
                </div>
            </details>
            <p className="admin-settings-section-hint lb-form-hint">{t("leaders_bonus_form_hint")}</p>
            <div className="lb-form-actions">
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDraft(null)} disabled={saving}>
                    {t("cancel")}
                </button>
                <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleSave}
                    disabled={saving || !draftValid(d)}
                >
                    {saving ? <Loader2 size={14} className="lb-spin" aria-hidden="true" /> : null}
                    {saving ? t("saving") : t("save")}
                </button>
            </div>
        </div>
    );

    if (loading) {
        return <div className="admin-settings-loading">{t("loading")}</div>;
    }

    return (
        <div className="lb-settings">
            {warehouses.map((wh) => {
                const list = byWarehouse[wh] ?? [];
                const addingHere = draft?.id === null && draft.warehouse === wh;
                return (
                    <details key={wh} className="admin-settings-po-warehouse">
                        <summary className="admin-settings-po-warehouse-summary">
                            <span className="admin-settings-po-warehouse-name">{wh}</span>
                            <span className="admin-settings-po-warehouse-count">{list.length}</span>
                        </summary>
                        {/* Collapsed per warehouse like the other per-warehouse
                            settings blocks; the summary carries the period count. */}
                        <div className="admin-settings-po-editor lb-warehouse">
                            {/* A div, not <header>: header.css styles every <header> as the dark app bar. */}
                            <div className="lb-warehouse-head">
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={() => setDraft(draftFor(wh, list[0], today))}
                                    disabled={draft !== null}
                                >
                                    <Plus size={14} aria-hidden="true" />
                                    {t("leaders_bonus_add_period")}
                                </button>
                            </div>

                            {addingHere && draft && form(draft)}

                            {list.length === 0 && !addingHere ? (
                                <p className="admin-settings-empty">{t("leaders_bonus_no_periods")}</p>
                            ) : list.length > 0 && (
                                <div className="lb-table" role="table">
                                    <div className="lb-row lb-row--head" role="row">
                                        <div>{t("leaders_bonus_col_period")}</div>
                                        <div>{t("leaders_bonus_col_working_days")}</div>
                                        <div>{t("leaders_bonus_col_on_time")}</div>
                                        <div>{t("leaders_bonus_col_booked_in")}</div>
                                        <div>{t("leaders_bonus_col_put_away")}</div>
                                        <div>{t("leaders_bonus_col_period_targets")}</div>
                                        <div aria-hidden="true" />
                                    </div>
                                    {list.map((p) => {
                                        const status = statusOf(p);
                                        const editing = draft?.id === p.id;
                                        return (
                                            <div key={p.id} className={`lb-row-wrap${editing ? " lb-row-wrap--editing" : ""}`}>
                                                <div className={`lb-row lb-row--${status}`} role="row">
                                                    <div className="lb-period">
                                                        <span className="lb-period-dates">{fmtDate(p.starts_on)} – {fmtDate(p.ends_on)}</span>
                                                        <span className={`lb-status lb-status--${status}`}>{t(`leaders_bonus_${status}`)}</span>
                                                    </div>
                                                    <div>{p.working_days}</div>
                                                    <div>{p.on_time_target_pct}%</div>
                                                    <div>{fmtInt(p.booked_in_daily_target)}</div>
                                                    <div>{fmtInt(p.put_away_daily_target)}</div>
                                                    <div className="lb-period-targets">
                                                        {t("leaders_bonus_period_targets", {
                                                            booked: fmtInt(p.booked_in_target),
                                                            put_away: fmtInt(p.put_away_target),
                                                        })}
                                                    </div>
                                                    <div className="dispatch-admin-row-actions-inline">
                                                        {deleteId === p.id ? (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-danger btn-sm"
                                                                    onClick={() => handleDelete(p.id)}
                                                                    disabled={deleting}
                                                                >
                                                                    {t("delete")}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-ghost btn-sm"
                                                                    onClick={() => setDeleteId(null)}
                                                                    disabled={deleting}
                                                                >
                                                                    {t("cancel")}
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    className="dispatch-admin-row-icon"
                                                                    title={t("edit")}
                                                                    aria-label={t("edit")}
                                                                    onClick={() => setDraft(draftOf(p))}
                                                                    disabled={draft !== null}
                                                                >
                                                                    <Pencil size={16} />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="dispatch-admin-row-icon dispatch-admin-row-icon--danger"
                                                                    title={t("delete")}
                                                                    aria-label={t("delete")}
                                                                    onClick={() => setDeleteId(p.id)}
                                                                    disabled={draft !== null}
                                                                >
                                                                    <Trash2 size={16} />
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                                {editing && draft && form(draft)}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </details>
                );
            })}
        </div>
    );
};

export default LeadersBonusSettings;
